import React, {
  memo,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useMemo,
} from "react";
import { Container, Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";
import { OVERLAY_BACKGROUND_COLOR } from "../Overlay/OverlayController";

import anime from "animejs";

// Dimmed symbol alpha (calculated from tint 0x555555)
const DIMMED_ALPHA = 0.8;

// Fade animation duration (ms)
const DIM_FADE_DURATION = 250;
const DIM_FADE_DURATION_FAST = 100;

// Buffer padding as a fraction of symbol height — scales uniformly across resolutions.
// At SYMBOL_HEIGHT=252, 0.016 ≈ 4px per side.
const DIM_PADDING_MULTIPLIER = 0.016;

// Unified dimming overlay for both main game and free spins
const DimReelSet = memo(
  forwardRef(({ symbolWidth, symbolHeight, dividerDimensions }, ref) => {
    const dimSpriteRef = useRef(null);
    const currentAlphaRef = useRef(0); // Track current alpha to persist across re-renders
    const fadeAnimRef = useRef(null); // Track active anime.js fade tween

    // Memoize sprite dimensions with padding buffer
    const spriteDimensions = useMemo(
      () => {
        const padding = Math.round(symbolHeight * DIM_PADDING_MULTIPLIER);
        return {
          width:
            symbolWidth * Settings.REEL_COUNT +
            dividerDimensions.width * (Settings.REEL_COUNT - 1) +
            padding * 2,
          height: symbolHeight * Settings.SYMBOLS_VISIBLE * 1.0 + padding * 2,
        };
      },
      [symbolWidth, symbolHeight, dividerDimensions.width],
    );

    // Expose dimming control methods through ref
    useImperativeHandle(
      ref,
      () => ({
        // Dim the reels — pass fast=true for quicker 100ms fade (win presentation)
        dimReels: (fast = false) => {
          if (!dimSpriteRef.current) return;

          // Cancel any in-progress fade
          if (fadeAnimRef.current) {
            fadeAnimRef.current.pause();
            fadeAnimRef.current = null;
          }

          currentAlphaRef.current = DIMMED_ALPHA;
          fadeAnimRef.current = anime({
            targets: dimSpriteRef.current,
            alpha: DIMMED_ALPHA,
            duration: fast ? DIM_FADE_DURATION_FAST : DIM_FADE_DURATION,
            easing: "easeOutQuad",
            complete: () => {
              fadeAnimRef.current = null;
            },
          });
        },

        // Remove dimming — pass instant=true for quick sequential fade (returns Promise)
        undimReels: (instant = false) => {
          if (!dimSpriteRef.current) return Promise.resolve();
          if (currentAlphaRef.current === 0 && !fadeAnimRef.current) return Promise.resolve();

          // Cancel any in-progress fade
          if (fadeAnimRef.current) {
            fadeAnimRef.current.pause();
            fadeAnimRef.current = null;
          }

          currentAlphaRef.current = 0;

          if (instant) {
            // Quick fade that resolves — caller awaits before clearing overlays/textures
            return new Promise((resolve) => {
              fadeAnimRef.current = anime({
                targets: dimSpriteRef.current,
                alpha: 0,
                duration: DIM_FADE_DURATION_FAST,
                easing: "easeOutQuad",
                complete: () => {
                  fadeAnimRef.current = null;
                  resolve();
                },
              });
            });
          } else {
            fadeAnimRef.current = anime({
              targets: dimSpriteRef.current,
              alpha: 0,
              duration: DIM_FADE_DURATION,
              easing: "easeInQuad",
              complete: () => {
                fadeAnimRef.current = null;
              },
            });
            return Promise.resolve();
          }
        },

        // Instant snap to alpha 0 — synchronous, no animation.
        // Used on spin start so reels never begin while dim is still visible.
        snapClear: () => {
          if (!dimSpriteRef.current) return;
          if (fadeAnimRef.current) {
            fadeAnimRef.current.pause();
            fadeAnimRef.current = null;
          }
          currentAlphaRef.current = 0;
          dimSpriteRef.current.alpha = 0;
        },
      }),
      [],
    );

    // Cleanup anime.js tween on unmount to prevent targeting destroyed sprites
    useEffect(() => {
      return () => {
        if (fadeAnimRef.current) {
          fadeAnimRef.current.pause();
          fadeAnimRef.current = null;
        }
      };
    }, []);

    // Preserve alpha state across re-renders (especially during window resize)
    useEffect(() => {
      if (dimSpriteRef.current && currentAlphaRef.current !== 0) {
        // Restore the alpha without animation when dimensions change
        dimSpriteRef.current.alpha = currentAlphaRef.current;
      }
    }, [symbolWidth, symbolHeight, dividerDimensions]);

    return (
      <Container name="dim_overlay" zIndex={2}>
        <Sprite
          ref={dimSpriteRef}
          texture={PIXI.Texture.WHITE}
          width={spriteDimensions.width}
          height={spriteDimensions.height}
          anchor={0.5}
          tint={OVERLAY_BACKGROUND_COLOR}
          alpha={0}
          name="dim_overlay_placeholder"
        />
      </Container>
    );
  }),
);

DimReelSet.displayName = "DimReelSet";

export default DimReelSet;
